// Stackly Office — Floor engine (Handoff 2). Plain ES module, no dependencies.
// Draws the isometric pixel-art office on a <canvas> and animates REAL events from GET /api/office/live.
// Put this file in frontend/src/ds/floor/ (pixel-art colours below are art data, allowed inside src/ds/).
//
// API
//   const floor = createFloor({ canvas, overlay, reducedMotion })
//   floor.setAgents(agents)      // agents[] from /api/office/live (id, name, department, status, activity, task)
//   floor.enqueue(events)        // events[] oldest -> newest (request | reply | meeting_start | meeting_end)
//   floor.snapshot(agents)       // after the tab was hidden: place everyone directly, drop pending animations
//   floor.start() / floor.stop() // start/stop the render loop (stop = zero CPU)
//   floor.resize(availableWidthPx)
//   floor.destroy()              // stop + remove overlay nodes
// Rule: nothing moves unless an event or a status from the API says so.

export function createFloor({ canvas, overlay, reducedMotion = false }) {
  const ctx = canvas.getContext('2d');
  canvas.width = 440; canvas.height = 256;
  ctx.imageSmoothingEnabled = false;
  const CW = canvas.width, CH = canvas.height, OX = 188, OY = 40;
  const P = (x, y) => [(x - y) * 8 + OX, (x + y) * 4 + OY];
  const up = (p, h) => [p[0], p[1] - h];

  function poly(pts, color, c = ctx) {
    c.fillStyle = color;
    let minY = Infinity, maxY = -Infinity;
    for (const p of pts) { minY = Math.min(minY, p[1]); maxY = Math.max(maxY, p[1]); }
    minY = Math.round(minY); maxY = Math.round(maxY);
    for (let y = minY; y < maxY; y++) {
      const yc = y + 0.5; let xl = Infinity, xr = -Infinity;
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i], b = pts[(i + 1) % pts.length];
        if ((a[1] <= yc && b[1] > yc) || (b[1] <= yc && a[1] > yc)) {
          const x = a[0] + (yc - a[1]) * (b[0] - a[0]) / (b[1] - a[1]);
          xl = Math.min(xl, x); xr = Math.max(xr, x);
        }
      }
      if (xl < xr) c.fillRect(Math.round(xl), y, Math.round(xr) - Math.round(xl), 1);
    }
  }
  function box(x0, y0, x1, y1, h, top, left, right, z = 0, c = ctx) {
    const A = up(P(x0, y0), z), B = up(P(x1, y0), z), C = up(P(x1, y1), z), D = up(P(x0, y1), z);
    poly([up(D, h), up(C, h), C, D], left, c);
    poly([up(C, h), up(B, h), B, C], right, c);
    poly([up(A, h), up(B, h), up(C, h), up(D, h)], top, c);
  }

  /* ---------- world ---------- */
  const DEPT = {
    ceo:     { name: 'CEO Office',     shirt: '#64748b', tint: ['#8a7f73', '#837869'] },
    product: { name: 'Product Studio', shirt: '#3f6cf0', tint: ['#9aa3ad', '#929ba5'] },
    meeting: { name: 'Meeting Room',   shirt: '#3f6cf0', tint: ['#7b6f62', '#74685b'] },
    legal:   { name: 'Legal',          shirt: '#6b4fd0', tint: ['#98a0aa', '#9098a2'] },
    it:      { name: 'IT',             shirt: '#1aa39a', tint: ['#9aa3ad', '#929ba5'] },
    mkt:     { name: 'Marketing',      shirt: '#ea7a2f', tint: ['#a19a92', '#99928a'] },
    sales:   { name: 'Sales',          shirt: '#3aa35b', tint: ['#9aa3ad', '#929ba5'] },
    fin:     { name: 'Finance',        shirt: '#d9a21b', tint: ['#a19a92', '#99928a'] },
  };
  const ROOMS = [
    { id: 'ceo',     x0: 0,  y0: 0,  x1: 4,  y1: 9,  door: 2,  side: 'S', aisle: 8 },
    { id: 'product', x0: 5,  y0: 0,  x1: 13, y1: 9,  door: 9,  side: 'S', aisle: 8 },
    { id: 'meeting', x0: 16, y0: 0,  x1: 23, y1: 9,  door: 19, side: 'S', aisle: 8 },
    { id: 'legal',   x0: 24, y0: 0,  x1: 29, y1: 9,  door: 26, side: 'S', aisle: 8 },
    { id: 'it',      x0: 0,  y0: 12, x1: 6,  y1: 21, door: 3,  side: 'N', aisle: 14 },
    { id: 'mkt',     x0: 7,  y0: 12, x1: 13, y1: 21, door: 10, side: 'N', aisle: 14 },
    { id: 'sales',   x0: 16, y0: 12, x1: 22, y1: 21, door: 19, side: 'N', aisle: 14 },
    { id: 'fin',     x0: 23, y0: 12, x1: 29, y1: 21, door: 26, side: 'N', aisle: 14 },
  ];
  const roomOf = id => ROOMS.find(r => r.id === id);
  const CORR_Y = 10.5;
  // Desk slots per room, filled in a stable order (agents sorted by name).
  const SLOTS = {
    ceo: [[3, 2]],
    product: [[6, 5], [8, 5], [10, 5], [12, 5], [6, 2.6], [8, 2.6], [10, 2.6], [12, 2.6]],
    legal: [[25, 5], [27, 5]],
    it: [[1, 16], [3, 16], [5, 16]],
    mkt: [[8, 16], [10, 16], [12, 16]],
    sales: [[17, 16], [19, 16], [21, 16]],
    fin: [[24, 16], [26, 16], [28, 16]],
  };
  const MEET_SEATS = [[18, 3], [19, 3], [20, 3], [21, 3], [18, 6], [19, 6], [20, 6], [21, 6], [17, 4.5], [22, 4.5]].map(([x, y]) => ({ x: x + 0.5, y: y + 0.5 }));

  function roomForDepartment(dep, name) {
    const d = String(dep || '').toLowerCase(), n = String(name || '').toLowerCase();
    if (d.includes('ceo') || n.includes('chief of staff')) return 'ceo';
    if (d.includes('legal')) return 'legal';
    if (d.includes('market')) return 'mkt';
    if (d.includes('sales')) return 'sales';
    if (d.includes('financ') || n === 'cfo') return 'fin';
    if (d === 'it' || d.startsWith('it ') || d.includes('support')) return 'it';
    return 'product';
  }
  function propFor(name) {
    const n = name.toLowerCase();
    if (n.includes('design') || n.includes('content')) return 'tablet';
    if (n.startsWith('it') || n.includes('triage') || n.includes('support')) return 'headset';
    if (n === 'cfo' || n.includes('billing') || n.includes('bookkeep')) return 'calculator';
    if (n.includes('legal') || n.includes('contract') || n.includes('privacy')) return 'books';
    if (n.includes('chief of staff')) return 'notepad';
    return (hash(n) % 2) ? 'mug' : 'none';
  }

  const SKIN = ['#f1c9a5', '#e0ac85', '#c68b62', '#a06a45', '#7a4b2e', '#5b3820'];
  const HAIR = ['#1f1a17', '#3b2a1e', '#6b4226', '#a8692e', '#d8b26a', '#b8b8b8', '#8c2f1d', '#2b2b3a'];
  const PANTS = ['#2b3442', '#3a3f4a', '#4a3b2e', '#2f3d33', '#22324f', '#5a5a5a'];
  const STYLES = ['short', 'long', 'bun', 'bald', 'curly', 'cap', 'side', 'spiky'];
  function hash(s) { let h = 2166136261; for (const ch of s) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619); } return h >>> 0; }

  /* ---------- static layer ---------- */
  const stat = document.createElement('canvas'); stat.width = CW; stat.height = CH;
  const sc = stat.getContext('2d');
  const GLYPH = {
    A:['01110','10001','10001','11111','10001','10001','10001'], C:['01111','10000','10000','10000','10000','10000','01111'],
    D:['11110','10001','10001','10001','10001','10001','11110'], E:['11111','10000','10000','11110','10000','10000','11111'],
    F:['11111','10000','10000','11110','10000','10000','10000'], G:['01111','10000','10000','10011','10001','10001','01111'],
    I:['11111','00100','00100','00100','00100','00100','11111'], K:['10001','10010','10100','11000','10100','10010','10001'],
    L:['10000','10000','10000','10000','10000','10000','11111'], M:['10001','11011','10101','10101','10001','10001','10001'],
    N:['10001','11001','10101','10011','10001','10001','10001'], O:['01110','10001','10001','10001','10001','10001','01110'],
    P:['11110','10001','10001','11110','10000','10000','10000'], R:['11110','10001','10001','11110','10100','10010','10001'],
    S:['01111','10000','10000','01110','00001','00001','11110'], T:['11111','00100','00100','00100','00100','00100','00100'],
    U:['10001','10001','10001','10001','10001','10001','01110'], ' ':['00000','00000','00000','00000','00000','00000','00000'],
  };
  const FLOOR_TEXT = {
    ceo: { lines: ['CEO', 'OFFICE'], at: [0.5, 6.4] }, product: { lines: ['PRODUCT', 'STUDIO'], at: [6.0, 0.6] },
    meeting: { lines: ['MEETING', 'ROOM'], at: [17.0, 0.6] }, legal: { lines: ['LEGAL'], at: [25.4, 2.2] },
    it: { lines: ['IT'], at: [2.0, 19.3] }, mkt: { lines: ['MARKETING'], at: [7.15, 19.1] },
    sales: { lines: ['SALES'], at: [17.6, 19.3] }, fin: { lines: ['FINANCE'], at: [24.3, 19.2] },
  };
  function paintFloorText(c) {
    for (const cfg of Object.values(FLOOR_TEXT)) {
      const w = Math.max(...cfg.lines.map(l => l.length)) * 6, h = cfg.lines.length * 9;
      const off = document.createElement('canvas'); off.width = w + 2; off.height = h + 2;
      const o = off.getContext('2d');
      cfg.lines.forEach((line, li) => [...line].forEach((ch, ci) => {
        (GLYPH[ch] || GLYPH[' ']).forEach((row, ry) => [...row].forEach((bit, rx) => {
          if (bit === '1') { o.fillStyle = 'rgba(20,26,32,0.28)'; o.fillRect(2 + ci * 6 + rx, 1 + li * 9 + ry, 1, 1); o.fillStyle = 'rgba(255,255,255,0.78)'; o.fillRect(1 + ci * 6 + rx, 1 + li * 9 + ry, 1, 1); }
        }));
      }));
      const [ox, oy] = P(cfg.at[0], cfg.at[1]);
      c.save(); c.imageSmoothingEnabled = false; c.setTransform(1, 0.5, 0, 1, ox, oy); c.drawImage(off, 0, 0); c.restore();
    }
  }
  function wallX(c, x, y, h, face, shade, windows) {
    const a = P(x, y), b = P(x + 1, y);
    poly([up(a, h), up(b, h), b, a], (x & 1) ? face : shade, c);
    if (windows && (x % 3 !== 2)) { poly([up(a, h - 6), up(b, h - 6), up(b, 8), up(a, 8)], '#9fd0ee', c); poly([up(a, h - 6), up(b, h - 6), up(b, h - 8), up(a, h - 8)], '#d7efff', c); }
  }
  function wallY(c, x, y, h, face, shade, windows) {
    const a = P(x, y), b = P(x, y + 1);
    poly([up(a, h), up(b, h), b, a], (y & 1) ? face : shade, c);
    if (windows && (y % 3 !== 2)) { poly([up(a, h - 6), up(b, h - 6), up(b, 8), up(a, 8)], '#8fc3e3', c); poly([up(a, h - 6), up(b, h - 6), up(b, h - 8), up(a, h - 8)], '#cbe8fb', c); }
  }
  function drawStatic() {
    const W = 30, H = 22;
    poly([P(0, H), P(W, H), up(P(W, H), -6), up(P(0, H), -6)], '#5a3a26', sc);
    poly([P(W, 0), P(W, H), up(P(W, H), -6), up(P(W, 0), -6)], '#6e4a31', sc);
    for (let x = 0; x < W; x++) for (let y = 0; y < H; y++) {
      const r = ROOMS.find(r => x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1);
      poly([P(x, y), P(x + 1, y), P(x + 1, y + 1), P(x, y + 1)], r ? DEPT[r.id].tint[(x + y) & 1] : (((x + y) & 1) ? '#b8c0c8' : '#c2c9d0'), sc);
    }
    poly([P(18, 3), P(22, 3), P(22, 7), P(18, 7)], '#2f3f6e', sc);
    poly([P(0.6, 3.6), P(3.4, 3.6), P(3.4, 5.4), P(0.6, 5.4)], '#5b3b3b', sc);
    paintFloorText(sc);
    for (let x = 0; x < W; x++) wallX(sc, x, 0, 24, '#d9dde1', '#b9c0c6', true);
    for (let y = 0; y < H; y++) wallY(sc, 0, y, 24, '#c8ced4', '#aab2ba', true);
    poly([up(P(0, 0), 24), up(P(W, 0), 24), up(P(W, 0), 26), up(P(0, 0), 26)], '#eef1f3', sc);
    poly([up(P(0, 0), 24), up(P(0, H), 24), up(P(0, H), 26), up(P(0, 0), 26)], '#e4e8eb', sc);
    for (let i = 0; i < 3; i++) poly([up(P(18 + i, 0), 19), up(P(19 + i, 0), 19), up(P(19 + i, 0), 9), up(P(18 + i, 0), 9)], '#f6f8fa', sc);
    poly([up(P(1, 0), 18), up(P(2, 0), 18), up(P(2, 0), 11), up(P(1, 0), 11)], '#3f6cf0', sc);
  }

  /* ---------- furniture (depth-sorted drawables) ---------- */
  let FIXED = [];   // room furniture (never changes)
  let DESKS = [];   // per-agent desks (rebuilt when the roster changes)
  const add = (list, depth, fn) => list.push({ depth, fn });
  function glassX(x, y) { add(FIXED, x + y - 0.55, () => { const a = P(x, y), b = P(x + 1, y); poly([up(a, 14), up(b, 14), b, a], 'rgba(170,215,240,0.38)'); poly([up(a, 14), up(b, 14), up(b, 12), up(a, 12)], '#e9f5fc'); poly([a, b, up(b, 1), up(a, 1)], '#7d8a96'); }); }
  function glassY(x, y) { add(FIXED, x + y - 0.45, () => { const a = P(x, y), b = P(x, y + 1); poly([up(a, 14), up(b, 14), b, a], 'rgba(160,205,232,0.34)'); poly([up(a, 14), up(b, 14), up(b, 12), up(a, 12)], '#e1eff8'); poly([a, b, up(b, 1), up(a, 1)], '#6f7c88'); }); }
  function plant(x, y) { add(FIXED, x + y, () => { box(x + 0.35, y + 0.35, x + 0.65, y + 0.65, 4, '#b06a3d', '#7f4a2a', '#955835'); const c = P(x + 0.5, y + 0.5); ctx.fillStyle = '#2f8a47'; ctx.fillRect(c[0] - 3, c[1] - 11, 7, 6); ctx.fillStyle = '#3fae5b'; ctx.fillRect(c[0] - 2, c[1] - 13, 5, 4); ctx.fillRect(c[0] - 4, c[1] - 9, 2, 3); ctx.fillRect(c[0] + 3, c[1] - 9, 2, 3); }); }
  function shelf(x, y, w) { add(FIXED, x + y + 0.1, () => { box(x + 0.1, y + 0.05, x + w - 0.1, y + 0.35, 16, '#7a5233', '#5a3a22', '#6a452a'); const cols = ['#c0392b', '#2e86de', '#f1c40f', '#27ae60', '#8e44ad', '#e67e22']; for (let i = 0; i < w * 6; i++) { const p = P(x + 0.15 + i * 0.13, y + 0.36); ctx.fillStyle = cols[i % 6]; ctx.fillRect(p[0], p[1] - 14, 1, 4); ctx.fillRect(p[0], p[1] - 8, 1, 4); } }); }
  function buildFixed() {
    FIXED = [];
    for (const r of ROOMS) {
      if (r.side === 'S') { for (let x = r.x0; x <= r.x1; x++) if (x !== r.door) glassX(x, r.y1 + 1); }
      else { for (let x = r.x0; x <= r.x1; x++) if (x !== r.door) glassX(x, r.y0); }
    }
    for (const [x, y0, y1] of [[5, 0, 9], [14, 0, 9], [16, 0, 9], [24, 0, 9], [7, 12, 21], [14, 12, 21], [16, 12, 21], [23, 12, 21]]) for (let y = y0; y <= y1; y++) glassY(x, y);
    add(FIXED, 18 + 4.5 + 1, () => { box(18, 4, 22, 6, 7, '#a7683b', '#744524', '#8c5530'); box(19.6, 4.6, 20.4, 5.4, 1, '#d9e3f0', '#b5c2d2', '#c7d3e1', 7); });
    for (const s of MEET_SEATS) add(FIXED, s.x + s.y - 0.4, () => box(s.x - 0.22, s.y - 0.22, s.x + 0.22, s.y + 0.22, 5, '#3f6cf0', '#2346b4', '#2f57d0'));
    add(FIXED, 1 + 4 + 0.5, () => { box(0.7, 4.0, 3.3, 4.8, 8, '#5d3a24', '#3e2516', '#4e301d'); box(1.6, 4.1, 2.4, 4.25, 7, '#2a2f36', '#1f242a', '#30363e', 8); });
    add(FIXED, 1 + 3 - 0.3, () => { box(1.6, 3.1, 2.4, 3.3, 15, '#1f242a', '#15191d', '#1a1e23'); box(1.6, 3.3, 2.4, 3.8, 6, '#2a2f36', '#1f242a', '#30363e'); });
    add(FIXED, 0.5 + 1.6, () => box(0.15, 0.6, 0.85, 2.4, 6, '#7b4c4c', '#5a3434', '#6a3e3e'));
    shelf(24.2, 0.2, 2); shelf(0.2, 12.4, 2); shelf(23.2, 12.4, 2);
    [[13, 0.4], [23, 0.4], [29, 9], [29, 21], [15, 3], [4, 0.6]].forEach(([x, y]) => plant(x, y));
    for (const [x, y] of [[14, 10], [15, 11.3]]) add(FIXED, x + y, () => { box(x + 0.3, y + 0.3, x + 0.7, y + 0.7, 10, '#e9eef2', '#b9c2ca', '#d1d9df'); box(x + 0.35, y + 0.35, x + 0.65, y + 0.65, 7, '#8fd1ff', '#5aa9df', '#74bdef', 10); });
  }
  function propOnDesk(a, dx, dy) {
    const p = a.prop;
    if (p === 'mug') box(dx + 0.15, dy + 0.55, dx + 0.27, dy + 0.67, 3, '#f1f1f1', '#cfcfcf', '#e0e0e0', 7);
    if (p === 'books') { box(dx + 0.1, dy + 0.5, dx + 0.32, dy + 0.75, 2, '#7a3b2e', '#5a2a20', '#6a3227', 7); box(dx + 0.12, dy + 0.52, dx + 0.3, dy + 0.72, 2, '#2e4f7a', '#203a5a', '#284569', 9); box(dx + 0.1, dy + 0.5, dx + 0.32, dy + 0.75, 2, '#d6c38a', '#b3a06a', '#c4b17a', 11); }
    if (p === 'calculator') box(dx + 0.12, dy + 0.5, dx + 0.3, dy + 0.7, 1, '#3c4652', '#2b333c', '#343d47', 7);
    if (p === 'notepad') box(dx + 0.12, dy + 0.5, dx + 0.34, dy + 0.72, 1, '#fff6c8', '#e2d9a6', '#efe6b5', 7);
    if (p === 'tablet') box(dx + 0.12, dy + 0.5, dx + 0.32, dy + 0.66, 1, '#1d2228', '#15191d', '#1a1e23', 7);
  }
  function buildDesks() {
    DESKS = [];
    for (const a of agents) {
      const [x, y] = a.seat, dx = x, dy = y + 1;
      add(DESKS, dx + dy + 0.2, () => {
        box(dx + 0.05, dy + 0.1, dx + 0.95, dy + 0.85, 7, '#c78a52', '#8e5c32', '#a86e3e');
        const working = a.base === 'working' && a.mode === 'seated';
        const glow = working ? ((Math.floor(t * 2 + a.phase * 9) % 7 === 0) ? '#9fe0ff' : '#5fb6ff') : (a.base === 'offline' ? '#3a4048' : '#2d5a86');
        box(dx + 0.3, dy + 0.15, dx + 0.75, dy + 0.3, 8, '#2a2f36', '#1f242a', '#30363e', 7);
        const s1 = up(P(dx + 0.3, dy + 0.15), 15), s2 = up(P(dx + 0.75, dy + 0.15), 15);
        poly([s1, s2, up(s2, -1), up(s1, -1)], glow);
        box(dx + 0.45, dy + 0.3, dx + 0.6, dy + 0.42, 2, '#2a2f36', '#1f242a', '#30363e', 7);
        propOnDesk(a, dx, dy);
      });
      add(DESKS, x + y - 0.35, () => { box(x + 0.25, y + 0.15, x + 0.75, y + 0.25, 13, '#2c4fb8', '#1d3a8f', '#2546a8'); box(x + 0.25, y + 0.25, x + 0.75, y + 0.75, 5, '#3f6cf0', '#2346b4', '#2f57d0'); });
    }
  }

  /* ---------- people ---------- */
  function person(a, sx, sy, mode, frame, hover) {
    const L = a.look, shirt = DEPT[a.room].shirt;
    ctx.globalAlpha = a.base === 'offline' ? 0.45 : 1;
    const r = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(sx + x, sy + y, w, h); };
    const lift = L.tall;
    if (mode !== 'seated') {
      r(-4, -1, 8, 2, 'rgba(0,0,0,0.28)');
      const s = frame % 2 ? 1 : 0;
      r(-2, -5 - lift, 2, 4 + lift - s, L.pants); r(1, -5 - lift, 2, 4 + lift - (1 - s), L.pants);
      r(-2, -1 - s, 2, 1, '#1a1a1a'); r(1, -1 - (1 - s), 2, 1, '#1a1a1a');
    }
    const by = mode === 'seated' ? -3 : -5 - lift;
    r(-3, by - 5, 6, 5, shirt); r(-1, by - 5, 2, 1, 'rgba(255,255,255,0.35)');
    const sw = mode === 'walking' ? (frame % 2 ? 1 : -1) : 0;
    r(-4, by - 5 + Math.max(0, sw), 1, 4, shirt); r(3, by - 5 + Math.max(0, -sw), 1, 4, shirt);
    r(-4, by - 1 + Math.max(0, sw), 1, 1, L.skin); r(3, by - 1 + Math.max(0, -sw), 1, 1, L.skin);
    const hy = by - 10;
    r(-2, hy, 5, 5, L.skin); r(-1, hy + 2, 1, 1, '#1b1b1b'); r(1, hy + 2, 1, 1, '#1b1b1b');
    if (L.glasses) { r(-2, hy + 2, 2, 1, '#111'); r(1, hy + 2, 2, 1, '#111'); r(0, hy + 2, 1, 1, '#555'); }
    const hc = L.hair;
    switch (L.style) {
      case 'short': r(-2, hy - 1, 5, 2, hc); r(-2, hy + 1, 1, 1, hc); r(2, hy + 1, 1, 1, hc); break;
      case 'long': r(-2, hy - 1, 5, 2, hc); r(-3, hy, 1, 6, hc); r(3, hy, 1, 6, hc); break;
      case 'bun': r(-2, hy - 1, 5, 2, hc); r(-1, hy - 3, 3, 2, hc); break;
      case 'bald': r(-3, hy + 1, 1, 2, hc); r(3, hy + 1, 1, 2, hc); break;
      case 'curly': r(-3, hy - 2, 7, 3, hc); r(-3, hy + 1, 1, 2, hc); r(3, hy + 1, 1, 2, hc); r(-2, hy - 3, 1, 1, hc); r(1, hy - 3, 1, 1, hc); break;
      case 'cap': r(-2, hy - 1, 5, 2, '#d63b3b'); r(-3, hy + 1, 3, 1, '#a82a2a'); break;
      case 'side': r(-2, hy - 1, 5, 1, hc); r(-2, hy, 3, 1, hc); r(-3, hy, 1, 4, hc); break;
      case 'spiky': r(-2, hy - 1, 5, 1, hc); r(-2, hy - 2, 1, 1, hc); r(0, hy - 2, 1, 1, hc); r(2, hy - 2, 1, 1, hc); break;
    }
    if (a.prop === 'headset') { r(-3, hy + 2, 1, 2, '#222'); r(3, hy + 2, 1, 2, '#222'); r(-3, hy, 1, 2, '#222'); r(3, hy, 1, 2, '#222'); r(-2, hy - 1 - (L.style === 'curly' ? 1 : 0), 5, 1, '#222'); }
    if (a.prop === 'tablet' && mode !== 'seated') r(3, by - 4, 2, 3, '#20252b');
    if (a.prop === 'notepad' && mode !== 'seated') r(3, by - 4, 2, 3, '#fff3b0');
    ctx.globalAlpha = 1;
    if (hover) { ctx.fillStyle = '#ffffff'; ctx.fillRect(sx - 1, sy + hy - 7, 3, 2); ctx.fillRect(sx, sy + hy - 5, 1, 1); }
    if (a.base === 'blocked' && a.mode === 'seated' && !hover) { ctx.fillStyle = '#ef5b5b'; ctx.fillRect(sx - 1, sy + hy - 7, 3, 4); ctx.fillStyle = '#fff'; ctx.fillRect(sx, sy + hy - 6, 1, 2); }
    return { x: sx - 5, y: sy + hy - 4, w: 11, h: -hy + 5 };
  }

  /* ---------- roster ---------- */
  let agents = [];
  const byId = id => agents.find(a => a.id === id);
  function assignSeats(list) {
    const used = {};
    const sorted = [...list].sort((a, b) => a.name.localeCompare(b.name));
    for (const a of sorted) {
      let room = a.room, slots = SLOTS[room], i = used[room] || 0;
      if (i >= slots.length) { room = 'product'; slots = SLOTS.product; i = used.product || 0; }   // overflow
      if (i >= slots.length) { a.seat = [14.2 + (i % 2), 9.4]; a.room = 'product'; continue; }
      a.seat = slots[i]; used[room] = i + 1; a.room = room;
    }
  }
  function makeAgent(src) {
    const h = hash(src.name);
    const room = roomForDepartment(src.department, src.name);
    return {
      id: src.id, name: src.name, department: src.department || DEPT[room].name, room,
      prop: src.prop || propFor(src.name),
      look: { skin: SKIN[h % 6], hair: HAIR[(h >> 3) % 8], style: STYLES[(h >> 6) % 8], pants: PANTS[(h >> 9) % 6], glasses: ((h >> 12) % 3) === 0, tall: ((h >> 14) % 3) === 0 ? 1 : 0 },
      phase: (h % 100) / 100, base: src.status || 'idle', apiStatus: src.status || 'idle',
      activity: src.activity || '', task: src.task || null,
      mode: 'seated', path: [], busy: false, pos: { x: 0, y: 0 }, seat: [0, 0],
    };
  }
  function setAgents(list) {
    const ids = new Set(list.map(a => a.id));
    let changed = list.length !== agents.length || list.some(a => !byId(a.id));
    agents = agents.filter(a => ids.has(a.id));
    for (const src of list) {
      let a = byId(src.id);
      if (!a) { a = makeAgent(src); agents.push(a); changed = true; }
      a.apiStatus = src.status || 'idle'; a.activity = src.activity || ''; a.task = src.task || null;
      if (!a.busy) a.base = a.apiStatus;
    }
    if (changed) {
      assignSeats(agents);
      for (const a of agents) if (!a.busy && a.mode === 'seated') a.pos = { x: a.seat[0] + 0.5, y: a.seat[1] + 0.5 };
      buildDesks();
    }
    for (const a of agents) placeForStatus(a, false);
  }
  // Agents whose API status is "meeting" but who are not in the room yet (e.g. after a snapshot) are placed directly.
  function placeForStatus(a, force) {
    if (a.busy && !force) return;
    if (a.apiStatus === 'meeting' && a.mode === 'seated') {
      const i = agents.filter(x => x.mode === 'standing' && x.inMeeting).length;
      const s = MEET_SEATS[i % MEET_SEATS.length];
      a.pos = { x: s.x, y: s.y }; a.mode = 'standing'; a.inMeeting = true;
    } else if (a.apiStatus !== 'meeting' && a.inMeeting && !a.busy) {
      a.inMeeting = false; a.mode = 'seated'; a.pos = { x: a.seat[0] + 0.5, y: a.seat[1] + 0.5 };
    }
  }

  /* ---------- movement ---------- */
  let speed = 1;
  function exitPath(a) {
    const r = roomOf(a.room), sx = a.seat[0] + 1.5, d = r.door + 0.5;
    return [{ x: sx, y: a.seat[1] + 0.5 }, { x: sx, y: r.aisle + 0.5 }, { x: d, y: r.aisle + 0.5 }, { x: d, y: CORR_Y }];
  }
  function enterPath(roomId, target) {
    const r = roomOf(roomId), d = r.door + 0.5;
    return [{ x: d, y: CORR_Y }, { x: d, y: r.aisle + 0.5 }, { x: target.x, y: r.aisle + 0.5 }, target];
  }
  function walk(a, points, then) { a.mode = 'walking'; a.path = points.slice(); a.onArrive = then; }
  function goHome(a, fromRoom, then) {
    const home = roomOf(a.room), hd = home.door + 0.5, sx = a.seat[0] + 1.5;
    const seatPts = [{ x: sx, y: a.seat[1] + 0.5 }, { x: a.seat[0] + 0.5, y: a.seat[1] + 0.5 }];
    if (fromRoom === a.room) return walk(a, [{ x: a.pos.x, y: a.pos.y }, ...seatPts], () => { a.mode = 'seated'; then && then(); });
    const r = roomOf(fromRoom), d = r.door + 0.5;
    walk(a, [{ x: a.pos.x, y: r.aisle + 0.5 }, { x: d, y: r.aisle + 0.5 }, { x: d, y: CORR_Y }, { x: hd, y: CORR_Y }, { x: hd, y: home.aisle + 0.5 }, { x: sx, y: home.aisle + 0.5 }, ...seatPts], () => { a.mode = 'seated'; then && then(); });
  }
  function step(a, dt) {
    if (a.mode !== 'walking' || !a.path.length) return;
    let left = dt * 2.6 * speed;
    while (left > 0 && a.path.length) {
      const p = a.path[0], dx = p.x - a.pos.x, dy = p.y - a.pos.y, d = Math.hypot(dx, dy);
      if (d <= left) { a.pos.x = p.x; a.pos.y = p.y; a.path.shift(); left -= d; }
      else { a.pos.x += dx / d * left; a.pos.y += dy / d * left; left = 0; }
    }
    if (!a.path.length) { a.mode = 'standing'; const f = a.onArrive; a.onArrive = null; f && f(); }
  }

  /* ---------- overlay: bubbles, meeting card, tooltip ---------- */
  let SCALE = 3;
  const fx = document.createElement('div'); fx.className = 'fl-fx'; overlay.appendChild(fx);
  function placeEl(el, ix, iy) { el.style.left = (ix * SCALE) + 'px'; el.style.top = (iy * SCALE) + 'px'; }
  function headOf(a) { const p = P(a.pos.x, a.pos.y); return [p[0], p[1] - (a.mode === 'seated' ? 15 : 18 + a.look.tall)]; }
  const bubbles = [];
  function say(a, text, cls, ms) {
    if (!text) return;
    const el = document.createElement('div'); el.className = 'fl-say ' + (cls || ''); el.textContent = text.length > 140 ? text.slice(0, 137) + '…' : text;
    fx.appendChild(el); bubbles.push({ el, a, until: performance.now() + ms / speed });
  }
  let meetCard = null;
  function showMeetCard(task) {
    hideMeetCard();
    meetCard = document.createElement('div'); meetCard.className = 'fl-card';
    const b = document.createElement('b'); b.textContent = task && task.id ? task.id + ' meeting' : 'Meeting';
    const s = document.createElement('span'); s.textContent = (task && task.title) || '';
    meetCard.append(b, s); fx.appendChild(meetCard);
  }
  function hideMeetCard() { if (meetCard) { meetCard.remove(); meetCard = null; } }
  const tip = document.createElement('div'); tip.className = 'fl-tip'; tip.hidden = true; fx.appendChild(tip);
  const STATUS_TEXT = { working: 'Working', idle: 'Idle', walking: 'On the way', meeting: 'In a meeting', blocked: 'Blocked', offline: 'Offline' };
  let hoverId = null; const hit = new Map();
  function renderTip(a) {
    tip.replaceChildren();
    const n = document.createElement('div'); n.className = 'fl-tip-n'; n.textContent = a.name;
    const r = document.createElement('div'); r.className = 'fl-tip-r'; r.textContent = a.department;
    const st = a.mode === 'walking' ? 'walking' : a.base;
    const c = document.createElement('span'); c.className = 'fl-chip s-' + st; c.textContent = STATUS_TEXT[st] || st;
    const now = document.createElement('div'); now.className = 'fl-tip-now';
    const sm = document.createElement('small'); sm.textContent = 'Right now';
    const act = document.createElement('div'); act.textContent = a.mode === 'walking' ? 'Walking over to a colleague' : (a.activity || '—');
    now.append(sm, act);
    tip.append(n, r, c, now);
    if (a.task) { const tk = document.createElement('div'); tk.className = 'fl-tip-task'; tk.textContent = (a.task.id ? a.task.id + ' · ' : '') + (a.task.title || ''); tip.append(tk); }
    tip.hidden = false;
  }
  function onMove(e) {
    const rc = canvas.getBoundingClientRect(), ix = (e.clientX - rc.left) / SCALE, iy = (e.clientY - rc.top) / SCALE;
    let found = null, best = -1;
    for (const a of agents) { const h = hit.get(a.id); if (h && ix >= h.x && ix <= h.x + h.w && iy >= h.y && iy <= h.y + h.h) { const d = a.pos.x + a.pos.y; if (d > best) { best = d; found = a; } } }
    hoverId = found ? found.id : null; canvas.style.cursor = found ? 'pointer' : 'default';
    if (!found) tip.hidden = true;
    if (!running) draw();
  }
  function onLeave() { hoverId = null; tip.hidden = true; if (!running) draw(); }
  canvas.addEventListener('mousemove', onMove);
  canvas.addEventListener('mouseleave', onLeave);

  /* ---------- timers (all cleared on snapshot/destroy) ---------- */
  const timers = new Set();
  function later(fn, ms) { const id = setTimeout(() => { timers.delete(id); fn(); }, ms); timers.add(id); return id; }
  function clearTimers() { timers.forEach(clearTimeout); timers.clear(); }

  /* ---------- event queue (real events only) ---------- */
  const queue = [];           // pending events, oldest first
  const waitingReply = new Map(); // "from>to" -> visitor agent waiting at the other desk
  let meeting = null;         // { task, team: [agents] }
  function enqueue(events) {
    for (const ev of events || []) queue.push(ev);
    pump();
  }
  // Ordering rule: events for the same agent run in order; events for other agents do not wait behind them.
  // Fast-forward rule: the right-hand feed is always real time; the floor catches up (faster walking, and
  // when far behind, events are applied without walking).
  function involved(ev) {
    if (ev.type === 'request' || ev.type === 'reply') return [ev.from, ev.to];
    if (ev.type === 'meeting_start') return ['__meeting__', ...(ev.agents || [])];
    if (ev.type === 'meeting_end') return ['__meeting__', ...(meeting ? meeting.team.map(a => a.id) : [])];
    return [];
  }
  function pump() {
    speed = queue.length > 5 ? 3 : queue.length > 2 ? 2 : 1;
    const reserved = new Set();
    for (let i = 0; i < queue.length; i++) {
      const ev = queue[i], ids = involved(ev);
      if (ids.some(id => reserved.has(id)) && !(ev.type === 'reply' && waitingReply.has(ev.from + '>' + ev.to))) { ids.forEach(id => reserved.add(id)); continue; }
      const ok = queue.length > 8 ? applyInstantly(ev) : tryStart(ev);
      if (ok) { queue.splice(i, 1); i--; } else ids.forEach(id => reserved.add(id));
    }
  }
  function applyInstantly(ev) {
    if (ev.type === 'meeting_end') { endMeeting(true); return true; }
    if (ev.type === 'reply') { const w = waitingReply.get(ev.from + '>' + ev.to); if (w) { w.done(); return true; } }
    if (ev.type === 'request' || ev.type === 'reply') { const a = byId(ev.from); if (a && !a.busy) say(a, ev.text, ev.type === 'reply' ? 'reply' : '', 1500); return true; }
    return tryStart(ev);
  }
  function tryStart(ev) {
    if (ev.type === 'request') {
      const a = byId(ev.from), b = byId(ev.to);
      if (!a || !b || a === b) return true;                       // unknown agent: drop
      if (a.busy || b.busy) return false;                         // wait until both are free
      if (a.base === 'offline') { say(b, ev.text, '', 3000); return true; }
      a.busy = true; b.busy = true; a.base = 'walking';
      const spot = { x: b.seat[0] + 1.5, y: b.seat[1] + 0.5 };
      walk(a, [...exitPath(a), ...enterPath(b.room, spot)], () => {
        say(a, ev.text, '', 3200);
        const key = b.id + '>' + a.id;
        const done = () => { waitingReply.delete(key); later(() => goHome(a, b.room, () => { a.busy = false; b.busy = false; a.base = a.apiStatus; pump(); }), 1800 / speed); };
        waitingReply.set(key, { done, at: performance.now() });
        pump();   // a reply that arrived while walking can play now
        // no reply within 20 s: walk back anyway
        later(() => { if (waitingReply.has(key)) done(); }, 20000 / speed);
      });
      return true;
    }
    if (ev.type === 'reply') {
      const key = ev.from + '>' + ev.to, w = waitingReply.get(key), a = byId(ev.from);
      if (w) { say(a, ev.text, 'reply', 2800); later(w.done, 2400 / speed); return true; }
      if (a && !a.busy) { say(a, ev.text, 'reply', 2800); return true; }
      return !a; // replier busy elsewhere: wait
    }
    if (ev.type === 'meeting_start') {
      if (meeting) return false;
      const team = (ev.agents || []).map(byId).filter(a => a && a.base !== 'offline');
      if (team.length < 2) return true;
      if (team.some(a => a.busy)) return false;
      meeting = { task: ev.task, team };
      let arrived = 0;
      team.forEach((a, i) => {
        a.busy = true; a.base = 'meeting'; a.inMeeting = true;
        if (a.mode !== 'seated') { a.pos = { x: a.seat[0] + 0.5, y: a.seat[1] + 0.5 }; a.mode = 'seated'; }
        walk(a, [...exitPath(a), ...enterPath('meeting', MEET_SEATS[i % MEET_SEATS.length])], () => {
          a.mode = 'standing';
          if (++arrived === team.length) showMeetCard(ev.task);
        });
      });
      // safety: a meeting never blocks the floor for more than 15 minutes
      meeting.timer = later(() => endMeeting(false), 15 * 60 * 1000);
      return true;
    }
    if (ev.type === 'meeting_end') { endMeeting(false); return true; }
    return true; // unknown types are ignored
  }
  function endMeeting(instant) {
    if (!meeting) return;
    clearTimeout(meeting.timer); timers.delete(meeting.timer); hideMeetCard();
    const team = meeting.team; meeting = null;
    team.forEach(m => {
      m.inMeeting = false;
      const back = () => { m.busy = false; m.base = m.apiStatus; pump(); };
      if (instant) { m.path = []; m.mode = 'seated'; m.pos = { x: m.seat[0] + 0.5, y: m.seat[1] + 0.5 }; back(); }
      else goHome(m, 'meeting', back);
    });
  }
  function snapshot(list) {
    queue.length = 0; waitingReply.clear(); clearTimers();
    if (meeting) { clearTimeout(meeting.timer); meeting = null; hideMeetCard(); }
    bubbles.splice(0).forEach(b => b.el.remove());
    for (const a of agents) { a.busy = false; a.path = []; a.onArrive = null; a.inMeeting = false; a.mode = 'seated'; a.pos = { x: a.seat[0] + 0.5, y: a.seat[1] + 0.5 }; }
    setAgents(list || []);
    for (const a of agents) placeForStatus(a, true);
    if (!running) draw();
  }

  /* ---------- render loop ---------- */
  let t = 0, last = 0, raf = 0, running = false;
  function draw() {
    ctx.clearRect(0, 0, CW, CH); ctx.drawImage(stat, 0, 0);
    const items = FIXED.concat(DESKS);
    for (const a of agents) {
      const d = a.mode === 'seated' ? a.seat[0] + a.seat[1] + 0.05 : a.pos.x + a.pos.y - 0.5;
      items.push({ depth: d, fn: () => {
        const p = P(a.pos.x, a.pos.y);
        const typing = a.mode === 'seated' && a.base === 'working' && !reducedMotion ? (Math.floor(t * 3 + a.phase * 7) % 2) : 0;
        const fr = a.mode === 'walking' ? Math.floor(t * 8) : 0;
        hit.set(a.id, person(a, p[0], p[1] - (a.mode === 'seated' ? 5 : 0), a.mode, fr, hoverId === a.id));
        if (typing) { ctx.fillStyle = a.look.skin; ctx.fillRect(p[0] - 3, p[1] - 7, 1, 1); }
      } });
    }
    items.sort((x, y) => x.depth - y.depth).forEach(i => i.fn());
    const nowMs = performance.now();
    for (let i = bubbles.length - 1; i >= 0; i--) {
      const b = bubbles[i]; const [hx, hy] = headOf(b.a); placeEl(b.el, hx, hy - 6);
      if (nowMs > b.until) { b.el.style.opacity = 0; if (nowMs > b.until + 300) { b.el.remove(); bubbles.splice(i, 1); } }
    }
    if (meetCard) { const p = P(20, 5); placeEl(meetCard, p[0], p[1] - 16); }
    if (hoverId) {
      const a = byId(hoverId);
      if (a) {
        renderTip(a); const [hx, hy] = headOf(a);
        const below = hy * SCALE - tip.offsetHeight - 30 < 0;
        const half = tip.offsetWidth / 2 / SCALE;
        placeEl(tip, Math.min(CW - half - 2, Math.max(half + 2, hx)), below ? hy + 22 : hy - 8);
        tip.style.transform = below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)';
      }
    }
  }
  function frame(now) {
    const dt = Math.min(0.05, (now - last) / 1000); last = now; t += dt;
    agents.forEach(a => step(a, dt));
    draw();
    raf = requestAnimationFrame(frame);
  }
  function start() { if (running) return; running = true; last = performance.now(); raf = requestAnimationFrame(frame); }
  function stop() { running = false; cancelAnimationFrame(raf); raf = 0; }
  function resize(avail) {
    const whole = Math.floor(avail / CW);
    SCALE = (whole >= 1 && whole * CW >= avail * 0.88) ? whole : Math.max(1, Math.min(avail / CW, 4));
    canvas.style.width = (CW * SCALE) + 'px'; canvas.style.height = (CH * SCALE) + 'px';
    overlay.style.width = canvas.style.width; overlay.style.height = canvas.style.height;
    if (!running) draw();
  }
  function destroy() {
    stop(); canvas.removeEventListener('mousemove', onMove); canvas.removeEventListener('mouseleave', onLeave);
    clearTimers(); queue.length = 0;
    fx.remove();
  }

  drawStatic(); buildFixed(); draw();
  return { setAgents, enqueue, snapshot, start, stop, resize, destroy,
           get queueLength() { return queue.length; }, debug: () => ({ queue: queue.map(e => e.type + ':' + (e.from || '') + '>' + (e.to || '')), busy: agents.filter(a => a.busy).map(a => a.id + '/' + a.mode), meeting: !!meeting, waiting: [...waitingReply.keys()] }), roomNames: Object.fromEntries(Object.entries(DEPT).map(([k, v]) => [k, v.name])) };
}
